import { Button } from '@/components/ui/Button';
import { YStack } from '@/components/ui/layout/layout';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

import { useNotesStoreQuery } from './assistant-notes.query';
import { NotesItemList } from './notes-item-list';
import { NotesTextArea } from './notes-text-area';

type AssistantNotesEditorProps = {
  /** Classes of the area the notes show in, the text area or the list, so the height goes here */
  className?: string;
  /** Classes of the frame around that area and its footer */
  wrapperClassName?: string;
};

/**
 * The Assistant Notes of the global store: a text area while the store is kept as text, a list
 * of notes from the first item on. The load on each visit decides the view.
 */
export const AssistantNotesEditor = ({
  className,
  wrapperClassName,
}: AssistantNotesEditorProps) => {
  const store = useNotesStoreQuery();

  // The notes of the last visit would show a text area the user can type into while this
  // visit's load may turn it into the list and drop the typed text
  if (store.data === undefined || !store.isFetchedAfterMount) {
    if (store.isError) {
      return (
        <YStack className="mt-2 items-start gap-3">
          <p role="alert" className="text-sm text-danger-11">
            Your notes could not be loaded.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void store.refetch()}
          >
            Try again
          </Button>
        </YStack>
      );
    }
    return (
      <Skeleton
        className={cn('mt-2 h-[16rem] w-full', className, wrapperClassName)}
      />
    );
  }
  if (store.data.mode === 'file') {
    return (
      <NotesTextArea
        savedText={store.data.text}
        className={className}
        wrapperClassName={wrapperClassName}
      />
    );
  }
  return (
    <NotesItemList
      items={store.data.items}
      className={className}
      wrapperClassName={wrapperClassName}
    />
  );
};
